// A EXIGENCIA DE AUTORIZACAO DO SERVIDOR, POR ORGAO (#282).
//
// Sem configuracao, nada muda: a consignacao entra como sempre entrou. Ligada,
// a inclusao (e o aumento acima da isencao de reajuste) so' desconta depois que
// o servidor autorizar, pela tela ou pelo codigo enviado ao e-mail.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, NumberField, SelectField } from "@atlas/ui/web";
import type { ConfigAutorizacaoServidor } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { mensagemDeErro } from "../../lib/erro-validacao";

const ROTULO_CANAL: Record<ConfigAutorizacaoServidor["canal"], string> = {
  ambos: "Pela tela ou por código no e-mail",
  web: "Só pela tela",
  token: "Só por código no e-mail",
};

/** A configuracao em uma frase, para quem so' quer saber se esta' ligada. */
export function resumoDaAutorizacao(c: ConfigAutorizacaoServidor): string {
  if (!c.exigeParaInclusao && !c.exigeParaAumento) return `Não exige autorização do servidor${c.padrao ? " (padrão)" : ""}.`;
  const partes = [
    c.exigeParaInclusao ? "inclusão" : null,
    c.exigeParaAumento ? `aumento acima de ${(c.isentaReajusteAteBps / 100).toLocaleString("pt-BR")}%` : null,
  ].filter(Boolean);
  return `Exige autorização para ${partes.join(" e ")} · ${ROTULO_CANAL[c.canal].toLowerCase()} · prazo de ${c.prazoDias} dias.`;
}

export function AutorizacaoDoOrgao({ unidadeId }: { unidadeId: number }) {
  const qc = useQueryClient();
  const chave = ["admin", "consignacao-solicitacoes", "autorizacao-config", unidadeId];
  const q = useQuery({ queryKey: chave, queryFn: () => atlas.admin.consignacaoSolicitacoes.autorizacaoConfig(unidadeId) });
  const [editando, setEditando] = useState<ConfigAutorizacaoServidor | null>(null);
  const [isencao, setIsencao] = useState("0");
  const salvar = useMutation({
    mutationFn: (c: ConfigAutorizacaoServidor) =>
      atlas.admin.consignacaoSolicitacoes.definirAutorizacaoConfig(unidadeId, {
        exigeParaInclusao: c.exigeParaInclusao,
        exigeParaAumento: c.exigeParaAumento,
        isentaReajusteAteBps: Math.round(Number(isencao.replace(",", ".")) * 100),
        canal: c.canal,
        prazoDias: c.prazoDias,
      }),
    onSuccess: async () => {
      setEditando(null);
      await qc.invalidateQueries({ queryKey: chave });
    },
  });
  const c = q.data;
  const abrir = () => {
    if (!c) return;
    setEditando(c);
    setIsencao(String(c.isentaReajusteAteBps / 100));
  };
  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700 }}>Autorização do servidor</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{c ? resumoDaAutorizacao(c) : "Carregando…"}</div>
        </div>
        {editando === null ? (
          <Button size="sm" variant="ghost" onClick={abrir} disabled={!c}>
            Alterar
          </Button>
        ) : null}
      </div>
      {editando ? (
        <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
          <label style={{ display: "flex", gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={editando.exigeParaInclusao}
              onChange={(e) => setEditando({ ...editando, exigeParaInclusao: e.target.checked })}
            />
            Exigir autorização para incluir
          </label>
          <label style={{ display: "flex", gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={editando.exigeParaAumento}
              onChange={(e) => setEditando({ ...editando, exigeParaAumento: e.target.checked })}
            />
            Exigir autorização para aumentar
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            <NumberField
              label="Reajuste que não exige (%)"
              value={isencao}
              min={0}
              max={100}
              onChange={(e) => setIsencao(e.target.value)}
              disabled={!editando.exigeParaAumento}
            />
            <SelectField
              label="Canal"
              value={editando.canal}
              onChange={(e) => setEditando({ ...editando, canal: e.target.value as ConfigAutorizacaoServidor["canal"] })}
              options={(Object.keys(ROTULO_CANAL) as ConfigAutorizacaoServidor["canal"][]).map((k) => ({ value: k, label: ROTULO_CANAL[k] }))}
            />
            <NumberField
              label="Prazo para autorizar (dias)"
              value={String(editando.prazoDias)}
              min={1}
              max={90}
              onChange={(e) => setEditando({ ...editando, prazoDias: Number(e.target.value) })}
            />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="sm" onClick={() => salvar.mutate(editando)} disabled={salvar.isPending}>
              Salvar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditando(null)} disabled={salvar.isPending}>
              Cancelar
            </Button>
          </div>
          {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível salvar.")}</p>}
        </div>
      ) : null}
    </Card>
  );
}
